import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Button } from '../components/UI/Button'
import { Input } from '../components/UI/Input'
import { Pod24CommonLayout } from '../layouts/Pod24CommonLayout'

interface CompanySignupForm {
  companyName: string
  representativeName: string
  businessRegistration: FileList
  password: string
  confirmPassword: string
  termsOfService: boolean
  privacyPolicy: boolean
  businessTerms: boolean
  marketing: boolean
}

export const CompanySignupPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('signup')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<CompanySignupForm>()

  const password = watch('password')

  const onSubmit = async (data: CompanySignupForm) => {
    console.log('Company signup data:', data)
    // TODO: 기업 회원가입 API 호출
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadedFile(file)
    }
  }

  return (
    <Pod24CommonLayout>
      <div className="min-h-screen flex items-center justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
        <div className="w-full space-y-6 sm:space-y-8" style={{maxWidth: '560px'}}>
        <div className="text-center" style={{paddingTop: '40px'}}>
          <div className="space-y-1 text-left">
            <p className="text-gray-900 font-normal" style={{fontSize: '24px'}}>회원가입 하고</p>
            <p className="text-gray-900 font-semibold" style={{fontSize: '24px'}}>서비스를 이용하세요.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-start items-center border-b border-gray-200" style={{marginTop: '36px', gap: '28px'}}>
          <button
            onClick={() => navigate('/signup')}
            className="py-2 sm:py-3 px-0 text-left font-bold border-b-2 transition-colors border-transparent text-gray-500 hover:text-gray-700"
            style={{fontSize: '16px'}}
          >
            일반회원
          </button>
          <button
            onClick={() => setActiveTab('signup')}
            className="py-2 sm:py-3 px-0 text-left font-bold border-b-2 transition-colors border-blue-500 text-blue-600"
            style={{fontSize: '16px'}}
          >
            기업회원
          </button>
        </div>

        <form className="mt-6 sm:mt-8 space-y-4 sm:space-y-6 text-left" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-3 sm:space-y-4">
            {/* 이름(대표자명) */}
            <div>
              <label className="block font-medium text-gray-700 mb-1 text-left" style={{fontSize: '16px'}}>
                이름(대표자명)
              </label>
              <Input
                type="text"
                placeholder="이메일을 입력해 주세요."
                {...register('representativeName', { required: '이름을 입력해주세요.' })}
                error={errors.representativeName?.message}
              />
            </div>

            {/* 업체명 */}
            <div>
              <label className="block font-medium text-gray-700 mb-1 text-left" style={{fontSize: '16px'}}>
                업체명
              </label>
              <Input
                type="text"
                placeholder="주요사업 업체명 입력해 주세요."
                {...register('companyName', { required: '업체명을 입력해주세요.' })}
                error={errors.companyName?.message}
              />
            </div>

            {/* 사업자등록증/고유번호증 */}
            <div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700" style={{fontSize: '16px'}}>사업자등록증/고유번호증</span>
                  <span className="text-xs text-gray-500">지원확장자 : JPG, JPEG, PNG, PDF(최대 10MB)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 p-3 border border-gray-300 rounded-md bg-gray-50">
                    <span className="text-sm text-gray-500">
                      {uploadedFile ? uploadedFile.name : '파일을 선택해주세요'}
                    </span>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                      {...register('businessRegistration', { required: '사업자등록증을 업로드해주세요.' })}
                      onChange={handleFileUpload}
                    />
                    <div className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors">
                      파일선택
                    </div>
                  </label>
                </div>
                {errors.businessRegistration && (
                  <p className="text-sm text-red-600">{errors.businessRegistration.message}</p>
                )}
              </div>
            </div>

            {/* 비밀번호 */}
            <div>
              <label className="block font-medium text-gray-700 mb-1 text-left" style={{fontSize: '16px'}}>
                비밀번호
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="영문 숫자 특수문자 조합 8-16자리"
                {...register('password', {
                  required: '비밀번호를 입력해주세요.',
                  minLength: { value: 8, message: '비밀번호는 8자 이상이어야 합니다.' },
                  maxLength: { value: 16, message: '비밀번호는 16자 이하여야 합니다.' },
                  validate: {
                    hasLetter: (value) => /[a-zA-Z]/.test(value) || '영문자를 포함해야 합니다.',
                    hasNumber: (value) => /[0-9]/.test(value) || '숫자를 포함해야 합니다.',
                    hasSpecial: (value) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(value) || '특수문자를 포함해야 합니다.',
                    validChars: (value) => /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/.test(value) || '영문, 숫자, 특수문자만 입력 가능합니다.'
                  }
                })}
                rightIcon={
                  <button
                    type="button"
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '숨기기' : '보기'}
                  </button>
                }
                error={errors.password?.message}
              />
            </div>

            {/* 비밀번호 확인 */}
            <div>
              <label className="block font-medium text-gray-700 mb-1 text-left" style={{fontSize: '16px'}}>
                비밀번호 확인
              </label>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="비밀번호를 다시 입력해 주세요."
                {...register('confirmPassword', { 
                  required: '비밀번호 확인을 입력해주세요.',
                  validate: value => value === password || '비밀번호가 일치하지 않습니다.'
                })}
                rightIcon={
                  <button
                    type="button"
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '숨기기' : '보기'}
                  </button>
                }
                error={errors.confirmPassword?.message}
              />
            </div>
          </div>

          {/* 약관 동의 */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">약관 선택</h3>
            
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('termsOfService', { required: '서비스 이용약관에 동의해주세요.' })}
                />
                <span className="ml-2 font-medium text-gray-700 text-sm">
                  (필수) 서비스 이용약관 동의
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('privacyPolicy', { required: '개인정보 처리방침에 동의해주세요.' })}
                />
                <span className="ml-2 font-medium text-gray-700 text-sm">
                  (필수) 개인정보 수집 및 이용 동의
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('businessTerms', { required: '기업회원 가입 시 유의사항에 동의해주세요.' })}
                />
                <span className="ml-2 font-medium text-gray-700 text-sm">
                  (필수) 기업회원 가입 시 유의사항
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('marketing')}
                />
                <span className="ml-2 font-medium text-gray-700 text-sm">
                  (선택) 이벤트, 할인, 신제품 소식 정보 안내 받기
                </span>
              </label>
            </div>

            {(errors.termsOfService || errors.privacyPolicy || errors.businessTerms) && (
              <div className="text-sm text-red-600">
                필수 약관에 동의해주세요.
              </div>
            )}
          </div>

          {/* 회원가입 버튼 */}
          <div style={{marginTop: '32px'}}>
            <Button
              type="submit"
              fullWidth
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-3 sm:px-4 transition-colors"
              style={{height: '48px', borderRadius: '8px', fontSize: '16px'}}
            >
              회원 가입하기
            </Button>
          </div>

          {/* SNS 간편 가입 */}
          <div className="text-center" style={{marginTop: '32px'}}>
            <Link
              to="/sns-signup"
              className="font-medium text-gray-600 hover:text-blue-600 text-sm underline"
            >
              SNS로 간편하게 가입하기
            </Link>
          </div>
        </form>
        </div>
      </div>
    </Pod24CommonLayout>
  )
}